import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Autocomplete,
  TextField,
  Stack,
  MenuItem,
  Paper,
} from '@mui/material';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import RestoreFromTrashIcon from '@mui/icons-material/RestoreFromTrash';
import EditIcon from '@mui/icons-material/Edit';
import CategoryIcon from '@mui/icons-material/Category';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import AddIcon from '@mui/icons-material/Add';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import { DataGrid } from '@mui/x-data-grid';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const emptyProductForm = {
  name: '',
  code: '',
  category: '',
  subCategory: '',
  description: '',
  defaultQuantity: 1,
  unitPrice: 0,
  billingCycle: 'oneshot',
  status: 'draft',
};

const emptyCategoryForm = {
  name: '',
  subCategory: '',
};

const SummaryCard = ({ title, value, icon, bgColor, borderColor, onClick, subtitle }) => (
  <Card
    onClick={onClick}
    sx={{
      cursor: onClick ? 'pointer' : 'default',
      backgroundColor: bgColor || 'background.paper',
      border: borderColor || '1px solid rgba(0,0,0,0.08)',
      transition: 'transform .08s ease',
      '&:hover': onClick ? { transform: 'translateY(-2px)' } : undefined,
    }}
  >
    <CardContent sx={{ display: 'grid', gap: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {icon}
        <Typography variant="subtitle2" color="text.secondary">
          {title}
        </Typography>
      </Box>
      <Typography variant="h5">{value}</Typography>
      {subtitle ? (
        <Typography variant="caption" color="text.secondary">
          {subtitle}
        </Typography>
      ) : null}
    </CardContent>
  </Card>
);

export default function Products() {
  const [rows, setRows] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [includeDeleted, setIncludeDeleted] = useState(false);

  const [openDeletedDialog, setOpenDeletedDialog] = useState(false);
  const [openProductDialog, setOpenProductDialog] = useState(false);
  const [openCategoryDialog, setOpenCategoryDialog] = useState(false);

  const [editingProductId, setEditingProductId] = useState(null);
  const [productForm, setProductForm] = useState(emptyProductForm);
  const [categoryForm, setCategoryForm] = useState(emptyCategoryForm);

  const [dialogError, setDialogError] = useState('');
  const [categoryDialogError, setCategoryDialogError] = useState('');
  const [savingProduct, setSavingProduct] = useState(false);
  const [savingCategory, setSavingCategory] = useState(false);

  const isEditMode = Boolean(editingProductId);

  const fetchCategories = async () => {
    try {
      const res = await api.get('/api/products/categories');
      setCategories(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error('Fetch categories failed:', e);
      setCategories([]);
    }
  };

  const fetchProducts = async (p = page, l = pageSize, q = search) => {
    setLoading(true);
    try {
      const res = await api.get('/api/products', {
        params: {
          page: p + 1,
          limit: l,
          search: q || undefined,
          includeDeleted,
        },
      });
      const data = res.data?.data || [];
      setRows(data);
      setTotal(res.data?.pagination?.total || data.length);
    } catch (e) {
      console.error('Fetch products failed:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchProducts(0, pageSize, '');
    setPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeDeleted]);

  const totalCount = total;
  const deletedCount = rows.filter((r) => !!r.deleted).length;
  const activeCount = rows.filter((r) => !r.deleted).length;

  const deletedRows = rows
    .filter((r) => r.deleted)
    .map((r, i) => ({
      id: r._id || i,
      name: r.name,
      category: r.category || '',
      status: r.status || '',
      deletedAt: r.deleted ? new Date(r.deleted).toLocaleString('fr-FR') : '',
    }));

  const categoryOptions = useMemo(() => {
    const set = new Set(categories.map((c) => (c.name || '').trim()).filter(Boolean));
    rows.forEach((row) => {
      if (row.category) set.add(String(row.category).trim());
    });
    return Array.from(set).sort();
  }, [categories, rows]);

  const subCategoryOptionsByCategory = useMemo(() => {
    const map = new Map();

    categories.forEach((category) => {
      const categoryName = (category.name || '').trim();
      if (!categoryName) return;
      map.set(
        categoryName,
        Array.isArray(category.subCategories)
          ? category.subCategories.filter(Boolean).map((value) => String(value).trim())
          : []
      );
    });

    rows.forEach((row) => {
      const categoryName = (row.category || '').trim();
      const subCategoryName = (row.subCategory || '').trim();
      if (!categoryName || !subCategoryName) return;
      const current = new Set(map.get(categoryName) || []);
      current.add(subCategoryName);
      map.set(categoryName, Array.from(current).sort());
    });

    return map;
  }, [categories, rows]);

  const currentSubCategoryOptions = useMemo(
    () => subCategoryOptionsByCategory.get(productForm.category) || [],
    [productForm.category, subCategoryOptionsByCategory]
  );

  const categoryDialogSubCategoryOptions = useMemo(
    () => subCategoryOptionsByCategory.get(categoryForm.name) || [],
    [categoryForm.name, subCategoryOptionsByCategory]
  );

  const openNewProductDialog = () => {
    setEditingProductId(null);
    setProductForm(emptyProductForm);
    setDialogError('');
    setOpenProductDialog(true);
  };

  const openEditProductDialog = (product) => {
    setEditingProductId(product._id);
    setDialogError('');
    setProductForm({
      name: product.name || '',
      code: product.code || '',
      category: product.category || '',
      subCategory: product.subCategory || '',
      description: product.description || '',
      defaultQuantity: Number(product.defaultQuantity ?? 1),
      unitPrice: Number(product.unitPrice ?? 0),
      billingCycle: product.billingCycle || 'oneshot',
      status: product.status || 'draft',
    });
    setOpenProductDialog(true);
  };

  const openCategoryManagerDialog = () => {
    setCategoryDialogError('');
    setCategoryForm({
      name: productForm.category || '',
      subCategory: '',
    });
    setOpenCategoryDialog(true);
  };

  const handleSaveProduct = async ({ createAnother = false } = {}) => {
    if (!productForm.name?.trim()) {
      setDialogError('Le nom est requis');
      return;
    }

    setSavingProduct(true);
    setDialogError('');
    try {
      const payload = {
        name: productForm.name.trim(),
        code: productForm.code || '',
        category: productForm.category || '',
        subCategory: productForm.subCategory || '',
        description: productForm.description || '',
        status: productForm.status || 'draft',
        billingCycle: productForm.billingCycle || 'oneshot',
        defaultQuantity: Number(productForm.defaultQuantity || 1),
        unitPrice: Number(productForm.unitPrice || 0),
      };

      if (isEditMode) {
        await api.put(`/api/products/${editingProductId}`, payload);
      } else {
        await api.post('/api/products', payload);
      }

      await fetchCategories();
      await fetchProducts(page, pageSize, search);

      if (!isEditMode && createAnother) {
        setProductForm((current) => ({ ...emptyProductForm, category: current.category || '' }));
        return;
      }

      setOpenProductDialog(false);
      setEditingProductId(null);
      setProductForm(emptyProductForm);
    } catch (e) {
      console.error('Save product failed:', e);
      setDialogError(isEditMode ? 'Échec de la mise à jour du produit' : 'Échec de la création du produit');
    } finally {
      setSavingProduct(false);
    }
  };

  const handleCreateCategory = async () => {
    if (!categoryForm.name?.trim()) {
      setCategoryDialogError('Le nom de catégorie est requis');
      return;
    }

    setSavingCategory(true);
    setCategoryDialogError('');
    try {
      await api.post('/api/products/categories', {
        name: categoryForm.name.trim(),
        subCategories: categoryForm.subCategory?.trim() ? [categoryForm.subCategory.trim()] : [],
      });

      await fetchCategories();
      setProductForm((current) => ({
        ...current,
        category: categoryForm.name.trim(),
        subCategory: categoryForm.subCategory?.trim() || current.subCategory,
      }));

      setOpenCategoryDialog(false);
      setCategoryForm(emptyCategoryForm);
    } catch (e) {
      console.error('Create category failed:', e);
      setCategoryDialogError('Échec de la création de la catégorie');
    } finally {
      setSavingCategory(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        id: 'products',
        columns: [
          {
            accessorKey: 'name',
            header: 'Nom',
            size: 220,
            Cell: ({ cell }) => (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Inventory2Icon fontSize="small" />
                <Typography variant="body2">{cell.getValue()}</Typography>
              </Box>
            ),
          },
          { accessorKey: 'code', header: 'Code', size: 90 },
          {
            accessorKey: 'category',
            header: 'Catégorie',
            size: 140,
            Cell: ({ cell }) => (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CategoryIcon fontSize="small" />
                <Typography variant="body2">{cell.getValue() || '—'}</Typography>
              </Box>
            ),
          },
          { accessorKey: 'subCategory', header: 'Sous-catégorie', size: 150 },
          {
            accessorKey: 'billingCycle',
            header: 'Périodicité',
            size: 120,
            Cell: ({ cell }) => {
              const value = cell.getValue();
              const map = {
                mensuel: { label: 'Mensuel', color: 'info' },
                annuel: { label: 'Annuel', color: 'warning' },
                oneshot: { label: 'One shot', color: 'default' },
              };
              const cfg = map[value] || { label: value || 'One shot', color: 'default' };
              return <Chip size="small" label={cfg.label} color={cfg.color} />;
            },
          },
          {
            accessorKey: 'defaultQuantity',
            header: 'Qté',
            size: 70,
            Cell: ({ cell }) => <Typography variant="body2">{Number(cell.getValue() || 1)}</Typography>,
          },
          {
            accessorKey: 'unitPrice',
            header: 'Prix unitaire HT',
            size: 130,
            Cell: ({ cell }) => <Typography variant="body2">{Number(cell.getValue() || 0).toFixed(2)} €</Typography>,
          },
          {
            id: 'totalHt',
            header: 'Total ligne',
            size: 120,
            accessorFn: (row) => Number(row.defaultQuantity || 1) * Number(row.unitPrice || 0),
            Cell: ({ cell }) => <Typography variant="body2" fontWeight={700}>{Number(cell.getValue() || 0).toFixed(2)} €</Typography>,
          },
          {
            accessorKey: 'status',
            header: 'Statut',
            size: 100,
            Cell: ({ cell }) => {
              const value = cell.getValue();
              const map = {
                active: { label: 'Actif', color: 'success' },
                draft: { label: 'Brouillon', color: 'default' },
                archived: { label: 'Archivé', color: 'warning' },
              };
              const cfg = map[value] || { label: value || '—', color: 'default' };
              return <Chip size="small" label={cfg.label} color={cfg.color} />;
            },
          },
          {
            id: 'deleted',
            header: 'Supprimé',
            size: 95,
            accessorFn: (row) => (!!row.deleted ? 'Supprimé' : 'Actif'),
            Cell: ({ row }) =>
              row.original.deleted ? <Chip size="small" label="Supprimé" color="error" /> : <Chip size="small" label="Actif" />,
          },
        ],
      },
    ],
    []
  );

  const table = useMaterialReactTable({
    columns,
    data: rows,
    state: { isLoading: loading, showProgressBars: loading },
    enableRowActions: true,
    enableColumnFilterModes: true,
    enableColumnOrdering: true,
    enableGrouping: true,
    enableColumnPinning: true,
    enableFacetedValues: true,
    initialState: {
      density: 'compact',
      columnPinning: {
        left: ['mrt-row-expand', 'mrt-row-select'],
        right: ['mrt-row-actions'],
      },
      pagination: { pageSize },
    },
    onPaginationChange: ({ pageIndex, pageSize: ps }) => {
      setPage(pageIndex);
      setPageSize(ps);
      fetchProducts(pageIndex, ps, search);
    },
    manualPagination: true,
    rowCount: totalCount,
    paginationDisplayMode: 'pages',
    positionToolbarAlertBanner: 'bottom',
    muiSearchTextFieldProps: { size: 'small', variant: 'outlined', placeholder: 'Rechercher…' },
    muiPaginationProps: {
      color: 'secondary',
      rowsPerPageOptions: [25, 50, 100, 200],
      shape: 'rounded',
      variant: 'outlined',
    },
    onGlobalFilterChange: (value) => {
      setSearch(value || '');
      fetchProducts(0, pageSize, value || '');
      setPage(0);
    },
    renderRowActions: ({ row }) => (
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Tooltip title="Éditer">
          <IconButton size="small" onClick={() => openEditProductDialog(row.original)}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        {!row.original.deleted ? (
          <Tooltip title="Supprimer">
            <IconButton
              size="small"
              onClick={async () => {
                try {
                  await api.delete(`/api/products/${row.original._id}`);
                  await fetchProducts(page, pageSize, search);
                } catch (e) {
                  console.error('Delete failed:', e);
                }
              }}
            >
              <DeleteForeverIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : (
          <Tooltip title="Restaurer">
            <IconButton
              size="small"
              color="primary"
              onClick={async () => {
                try {
                  await api.post(`/api/products/${row.original._id}/restore`);
                  await fetchProducts(page, pageSize, search);
                } catch (e) {
                  console.error('Restore failed:', e);
                }
              }}
            >
              <RestoreFromTrashIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    ),
    renderTopToolbarCustomActions: () => (
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<DeleteForeverIcon />}
          color={includeDeleted ? 'error' : 'inherit'}
          onClick={() => setIncludeDeleted((value) => !value)}
        >
          {includeDeleted ? 'Masquer supprimés' : 'Afficher supprimés'}
        </Button>
        <Button variant="outlined" size="small" startIcon={<PlaylistAddIcon />} onClick={openCategoryManagerDialog}>
          Catégories
        </Button>
        <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={openNewProductDialog}>
          Nouveau produit
        </Button>
      </Box>
    ),
  });

  const productLineTotal = Number(productForm.defaultQuantity || 0) * Number(productForm.unitPrice || 0);

  return (
    <Box>
      <Typography variant="h6" my={1}>Produits</Typography>

      <Grid container spacing={2} my={2}>
        <Grid size={{ xs: 12, md: 3 }}>
          <SummaryCard title="Total" value={totalCount} icon={<Avatar sx={{ bgcolor: 'black' }}><Inventory2Icon /></Avatar>} />
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <SummaryCard title="Actifs" value={activeCount} icon={<Avatar sx={{ bgcolor: 'success.main' }}><AssignmentTurnedInIcon /></Avatar>} />
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <SummaryCard
            title="Supprimés"
            value={deletedCount}
            icon={<Avatar sx={{ bgcolor: 'error.main' }}><DeleteForeverIcon /></Avatar>}
            bgColor={deletedCount > 0 ? '#ffebee' : undefined}
            borderColor={deletedCount > 0 ? '1px solid #d32f2f' : undefined}
            onClick={() => setOpenDeletedDialog(true)}
          />
        </Grid>
      </Grid>

      <Box>
        <Typography variant="h6" sx={{ mb: 2 }}>Liste des produits</Typography>
        <MaterialReactTable table={table} />
      </Box>

      <Dialog open={openDeletedDialog} onClose={() => setOpenDeletedDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Produits supprimés</DialogTitle>
        <DialogContent>
          <DataGrid
            autoHeight
            rows={deletedRows}
            columns={[
              { field: 'name', headerName: 'Nom', flex: 1 },
              { field: 'category', headerName: 'Catégorie', flex: 1 },
              { field: 'status', headerName: 'Statut', flex: 1 },
              { field: 'deletedAt', headerName: 'Supprimé le', flex: 1 },
            ]}
            pageSize={10}
            rowsPerPageOptions={[10, 20, 50]}
            density="compact"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeletedDialog(false)}>Fermer</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openProductDialog} onClose={() => setOpenProductDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>{isEditMode ? 'Modifier le produit' : 'Nouveau produit'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Nom produit"
                  value={productForm.name}
                  onChange={(e) => setProductForm((current) => ({ ...current, name: e.target.value }))}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 2 }}>
                <TextField
                  fullWidth
                  label="Code"
                  value={productForm.code}
                  onChange={(e) => setProductForm((current) => ({ ...current, code: e.target.value }))}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 2 }}>
                <TextField
                  select
                  fullWidth
                  label="Statut"
                  value={productForm.status}
                  onChange={(e) => setProductForm((current) => ({ ...current, status: e.target.value }))}
                >
                  <MenuItem value="draft">Brouillon</MenuItem>
                  <MenuItem value="active">Actif</MenuItem>
                  <MenuItem value="archived">Archivé</MenuItem>
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, md: 2 }}>
                <TextField
                  select
                  fullWidth
                  label="Périodicité"
                  value={productForm.billingCycle}
                  onChange={(e) => setProductForm((current) => ({ ...current, billingCycle: e.target.value }))}
                >
                  <MenuItem value="oneshot">One shot</MenuItem>
                  <MenuItem value="mensuel">Mensuel</MenuItem>
                  <MenuItem value="annuel">Annuel</MenuItem>
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Autocomplete
                  freeSolo
                  options={categoryOptions}
                  value={productForm.category}
                  onChange={(_, value) => setProductForm((current) => ({ ...current, category: value || '', subCategory: '' }))}
                  onInputChange={(_, value) => setProductForm((current) => ({ ...current, category: value || '', subCategory: '' }))}
                  renderInput={(params) => <TextField {...params} label="Catégorie" fullWidth />}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Autocomplete
                  freeSolo
                  options={currentSubCategoryOptions}
                  value={productForm.subCategory}
                  onChange={(_, value) => setProductForm((current) => ({ ...current, subCategory: value || '' }))}
                  onInputChange={(_, value) => setProductForm((current) => ({ ...current, subCategory: value || '' }))}
                  renderInput={(params) => <TextField {...params} label="Sous-catégorie" fullWidth />}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Button variant="outlined" fullWidth sx={{ height: '56px' }} startIcon={<PlaylistAddIcon />} onClick={openCategoryManagerDialog}>
                  Créer catégorie / sous-catégorie
                </Button>
              </Grid>
            </Grid>

            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 2,
                background: 'linear-gradient(180deg, #1d7a57 0%, #176b4b 100%)',
                color: '#f2fff7',
                borderColor: 'rgba(189, 255, 220, 0.45)',
              }}
            >
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
                Composition du produit
              </Typography>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: '2fr 1fr 1fr 1fr' },
                  gap: 1.5,
                  alignItems: 'center',
                  pb: 1,
                  borderBottom: '1px solid rgba(255,255,255,0.35)',
                  color: 'rgba(242,255,247,0.92)',
                }}
              >
                <Typography variant="body2" fontWeight={600}>Produit</Typography>
                <Typography variant="body2" fontWeight={600}>Qté</Typography>
                <Typography variant="body2" fontWeight={600}>Prix unitaire HT</Typography>
                <Typography variant="body2" fontWeight={600}>Total ligne</Typography>
              </Box>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: '2fr 1fr 1fr 1fr' },
                  gap: 1.5,
                  alignItems: 'center',
                  pt: 1.5,
                }}
              >
                <Box>
                  <Typography variant="body1" fontWeight={700}>
                    {productForm.name || 'Nom du produit'}
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                    {productForm.category ? <Chip size="small" label={productForm.category} /> : null}
                    {productForm.subCategory ? <Chip size="small" label={productForm.subCategory} /> : null}
                  </Stack>
                </Box>

                <TextField
                  type="number"
                  size="small"
                  value={productForm.defaultQuantity}
                  onChange={(e) => setProductForm((current) => ({ ...current, defaultQuantity: e.target.value }))}
                  inputProps={{ min: 0, step: 1 }}
                  sx={{ '& .MuiInputBase-root': { backgroundColor: 'rgba(19, 84, 60, 0.9)', color: '#f2fff7' } }}
                />

                <TextField
                  type="number"
                  size="small"
                  value={productForm.unitPrice}
                  onChange={(e) => setProductForm((current) => ({ ...current, unitPrice: e.target.value }))}
                  inputProps={{ min: 0, step: '0.01' }}
                  sx={{ '& .MuiInputBase-root': { backgroundColor: 'rgba(19, 84, 60, 0.9)', color: '#f2fff7' } }}
                />

                <Typography variant="h6" fontWeight={800}>
                  {productLineTotal.toFixed(2)} €
                </Typography>
              </Box>
            </Paper>

            <TextField
              fullWidth
              label="Description"
              value={productForm.description}
              onChange={(e) => setProductForm((current) => ({ ...current, description: e.target.value }))}
            />

            {dialogError ? <Typography color="error">{dialogError}</Typography> : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenProductDialog(false)} disabled={savingProduct}>Annuler</Button>
          {!isEditMode ? (
            <Button onClick={() => handleSaveProduct({ createAnother: true })} disabled={savingProduct}>
              Enregistrer et créer un autre
            </Button>
          ) : null}
          <Button variant="contained" onClick={() => handleSaveProduct({ createAnother: false })} disabled={savingProduct}>
            {isEditMode ? 'Mettre à jour' : 'Enregistrer'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openCategoryDialog} onClose={() => setOpenCategoryDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Créer catégorie / sous-catégorie</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <Autocomplete
              freeSolo
              options={categoryOptions}
              value={categoryForm.name}
              onChange={(_, value) => setCategoryForm((current) => ({ ...current, name: value || '' }))}
              onInputChange={(_, value) => setCategoryForm((current) => ({ ...current, name: value || '' }))}
              renderInput={(params) => <TextField {...params} label="Catégorie" fullWidth />}
            />
            <Autocomplete
              freeSolo
              options={categoryDialogSubCategoryOptions}
              value={categoryForm.subCategory}
              onChange={(_, value) => setCategoryForm((current) => ({ ...current, subCategory: value || '' }))}
              onInputChange={(_, value) => setCategoryForm((current) => ({ ...current, subCategory: value || '' }))}
              renderInput={(params) => <TextField {...params} label="Sous-catégorie (optionnel)" fullWidth />}
            />
            {categoryDialogError ? <Typography color="error">{categoryDialogError}</Typography> : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCategoryDialog(false)} disabled={savingCategory}>Annuler</Button>
          <Button variant="contained" onClick={handleCreateCategory} disabled={savingCategory}>Enregistrer</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
